import { createAsyncThunk } from '@reduxjs/toolkit';
import { ApiException } from '@/api/exceptions/ApiException';
import api from '@/api/apiSingleton';
import { withComponentErrorHandling, withDefaultErrorHandling } from '@/store/utils';
import { localStorage } from '@/utils/storage';
import { saveAnswers } from '../survey/actions';
import { clearStore as clearUserStore, setIsOnboardingSurveyCompleted } from './userSlice';
import analytics from '@/utils/analytics/analyticsInstance';
import { Connection } from '@/types/plaid';
import { setIsPlaidLoginRequired } from '../plaid/plaidSlice';
import { RootState } from '@/store/store';
import { getCookie } from '@/utils/cookies';
import { sendAnalyticsEvent } from '../events/actions';

export const getUser = createAsyncThunk(
  'user/getUser',
  withDefaultErrorHandling(async (_: undefined) => {
    const response = await api.user.show();

    return response.data;
  }),
);

export const requestOTPCode = createAsyncThunk(
  'user/requestOTPCode',
  withDefaultErrorHandling(
    withComponentErrorHandling(
      async (payload: {
        phoneNumber: string;
        context: { place: string };
        callback?: () => void;
      }) => {
        try {
          await api.session.requestOTPCode({ phoneNumber: payload.phoneNumber });
          payload.callback?.();
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Unexpected error';

          analytics.track('authFailed', {
            place: payload.context.place,
            reason: errorMessage,
          });

          throw error;
        }
      },
    ),
  ),
);

export const confirmOTPCode = createAsyncThunk(
  'user/confirmOTPCode',
  withDefaultErrorHandling(
    withComponentErrorHandling(
      async (
        payload: {
          code: string;
          phoneNumber: string;
          context: { location: string; place: string };
          onSuccess?: (token: string, isLoginRequired: boolean) => void;
          onError?: (error: ApiException) => void;
        },
        thunkApi,
      ) => {
        try {
          const state = thunkApi.getState() as RootState;
          const { code, phoneNumber } = payload;
          const data = await api.session.confirmOTPCode({
            code,
            phoneNumber,
          });
          let isLoginRequired = false;

          await analytics.forceTrack('authSucceeded', {
            place: payload.context.place,
            type: data.isNewUser ? 'new registration' : 'login',
          });

          localStorage.save('accessToken', data.accessToken);

          await sendFBParams();
          await sendTikTokParams();

          const { data: connections } = await api.plaid.getConnections();

          if (connections.length) {
            isLoginRequired = connections.some(
              (connection: Connection) => connection.isLoginRequired,
            );

            await thunkApi.dispatch(setIsPlaidLoginRequired(isLoginRequired));
          }

          const listAnswers = await api.survey.listAnswers({
            surveyName: 'Onboarding',
          });

          let isOnboardingSurveyCompleted = Boolean(listAnswers.data.length);

          if (!isOnboardingSurveyCompleted) {
            const surveyResult = localStorage.get('surveyResult');

            if (surveyResult) {
              await thunkApi.dispatch(saveAnswers(surveyResult));

              isOnboardingSurveyCompleted = true;
            }
          }

          if (!state.user.isUserFromMobile) {
            await api.survey.setSelectedTags(state.survey.feedTags);
            await api.survey.saveAnswers({
              name: 'Rewards estimated',
              answers: state.survey.frequencyAnswers,
            });
            await api.survey.saveAnswers({
              name: 'Personalize your shopping',
              answers: [
                { question: 'What stores do you love to shop at?', answer: state.survey.stores },
              ],
            });
          }

          await thunkApi.dispatch(setIsOnboardingSurveyCompleted(isOnboardingSurveyCompleted));

          const user = await thunkApi.dispatch(getUser()).unwrap();

          if (data.isNewUser) {
            analytics.track('completeRegistration', {
              user_id: user.id,
              user_phone: user.phone,
            });
            thunkApi.dispatch(sendAnalyticsEvent({ eventName: 'completeRegistration' }));
          }

          payload.onSuccess?.(data.accessToken, isLoginRequired);
          analytics.setUserId(String(user.id));

          return user;
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Unexpected error';

          analytics.track('authFailed', {
            place: payload.context.place,
            reason: errorMessage,
          });

          if (error instanceof ApiException) {
            payload.onError?.(error);
          }

          throw error;
        }
      },
    ),
  ),
);

const sendFBParams = async () => {
  const fbc = localStorage.get('fbclid');
  const fbp = getCookie('_fbp');

  if (fbc || fbp) {
    await api.apiClient.put('/facebook/click-id', {
      fbc: fbc || undefined,
      fbp: fbp || undefined,
    });

    localStorage.remove('fbclid');
  }
};

const sendTikTokParams = async () => {
  const ttclid = localStorage.get('ttclid');
  const ttp = getCookie('_ttp');

  if (ttclid || ttp) {
    await api.apiClient.put('/tiktok/click-id', {
      ttclid: ttclid || undefined,
      ttp: ttp || undefined,
    });

    localStorage.remove('ttclid');
  }
};

// USED ONLY FOR TESTING
export const logout = createAsyncThunk(
  'user/logout',
  withDefaultErrorHandling(async (_, thunkApi) => {
    analytics.reset();
    localStorage.clear();
    thunkApi.dispatch(clearUserStore());
  }),
);
